import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { usePermissions } from "../../PermissionsProvider";
import { Agent } from "../../model_gen/agent_base";
import { Application } from "../../model_gen/application";
import { AgentService } from "../../services/AgentService";
import { ApplicationService } from "../../services/ApplicationService";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { ArrowLeft, Save, Play } from "lucide-react";
import AgentBasicInfo from "./AgentBasicInfo";
// import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";

interface AgentBuilderProps {
  mode: "create" | "edit";
}

const AgentBuilder: React.FC<AgentBuilderProps> = ({ mode }) => {
  const { apiKey } = usePermissions();
  const navigate = useNavigate();
  const { agentId } = useParams<{ agentId: string }>();
  const [searchParams] = useSearchParams();
  
  const [agent, setAgent] = useState<Partial<Agent & { base_url?: string }>>({
    agent_name: "",
    description: "",
    application_id: -1,
    base_url: "",
    meta: {}
  });
  const [applications, setApplications] = useState<Application[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showActionsSection, setShowActionsSection] = useState(false);
  const [parentAgentId, setParentAgentId] = useState<string | null>(null);

  useEffect(() => {
    loadApplications();
    if (mode === "edit" && agentId) {
      const parsedAgentId = parseInt(agentId);
      if (!isNaN(parsedAgentId)) {
        loadAgent(parsedAgentId);
      }
    }
  }, [apiKey, mode, agentId]);

  // Pre-select application from query parameter and get parent agent ID
  useEffect(() => {
    const applicationId = searchParams.get("application_id");
    const parentId = searchParams.get("parent_agent_id");
    
    if (applicationId && mode === "create") {
      const parsedId = parseInt(applicationId);
      if (!isNaN(parsedId)) {
        setAgent(prev => ({
          ...prev,
          application_id: parsedId
        }));
      }
    }
    
    if (parentId && mode === "create") {
      setParentAgentId(parentId);
    }
  }, [searchParams, mode]);

  const loadApplications = () => {
    if (!apiKey) return;
    
    ApplicationService.getApplications(() => {}, apiKey).subscribe(
      (response: any) => {
        const apps = Array.isArray(response) ? response : (response.applications || []);
        setApplications(apps);
      },
      (error: any) => {
        console.error("Error loading applications:", error);
      }
    );
  };

  const handleApplicationCreated = (newApplication: Application) => {
    // Add the new application to the list
    setApplications(prev => [...prev, newApplication]);
  };

  const loadAgent = (id: number) => {
    if (!apiKey) return;
    
    setIsLoading(true);
    AgentService.getAgent(id, setIsLoading, apiKey).subscribe(
      (response: Agent) => {
        setAgent(response);
        setIsLoading(false);
      },
      (error: any) => {
        console.error("Error loading agent:", error);
        setIsLoading(false);
      }
    );
  };

  const handleSave = async () => {
    if (!apiKey || !agent.agent_name || !agent.description || agent.application_id === -1) {
      return;
    }

    // Validate base_url if provided
    if (agent.base_url && agent.base_url.trim()) {
      try {
        new URL(agent.base_url);
      } catch (error) {
        console.error("Invalid base URL format");
        return;
      }
    }

    setIsSaving(true);

    // Prepare agent data with base_url
    const agentData = {
      ...agent,
      base_url: agent.base_url?.trim() || undefined // Remove empty strings
    };

    const saveOperation = mode === "create" 
      ? AgentService.createAgent(agentData, setIsSaving, apiKey)
      : AgentService.updateAgent(agent.id!, agentData, setIsSaving, apiKey);

    saveOperation.subscribe(
      (response: Agent) => {
        if (mode === "create") {
          // If this is a sub-agent being created from a parent agent, add the relationship
          if (parentAgentId && response.id) {
            const parsedParentId = parseInt(parentAgentId);
            if (!isNaN(parsedParentId)) {
              AgentService.addSubAgent(
                parsedParentId,
                response.id,
                () => {},
                apiKey
              ).subscribe(
                () => {
                  console.log("Successfully added sub-agent relationship");
                  // Navigate back to the parent agent details page
                  navigate(`/agent/${parentAgentId}`);
                },
                (error: any) => {
                  console.error("Error adding sub-agent relationship:", error);
                  // Still navigate to the new agent even if relationship failed
                  navigate(`/agent/${response.id}`);
                }
              );
            } else {
              console.error("Invalid parent agent ID:", parentAgentId);
              // Navigate to the new agent if parent ID is invalid
              navigate(`/agent/${response.id}`);
            }
          } else {
            // Navigate to the agent details page where capabilities can be configured
            navigate(`/agent/${response.id}`);
          }
        } else {
          setAgent(response);
        }
        setIsSaving(false);
      },
      (error: any) => {
        console.error("Error saving agent:", error);
        setIsSaving(false);
      }
    );
  };

  const handleTest = () => {
    // TODO: Implement agent testing functionality
    console.log("Testing agent...");
  };

  const isFormValid = agent.agent_name && agent.description && agent.application_id !== -1;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-8 py-8 space-y-8">
        
        {/* Header */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button 
                    onClick={() => navigate("/")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Home
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <button 
                    onClick={() => navigate("/forge")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Forge
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <button 
                    onClick={() => navigate("/forge/agents")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Agents
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">
                    {mode === "create" ? (parentAgentId ? "Create Sub-Agent" : "Create") : "Edit"}
                  </span>
                </li>
              </ol>
            </nav>

            {/* Page Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/forge/agents")}
                  className="gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </Button>
                <div className="space-y-1">
                  <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                    {mode === "create" ? (parentAgentId ? "Create Sub-Agent" : "Create Agent") : agent.agent_name}
                  </h1>
                  <p className="text-lg text-muted-foreground font-gc-reg">
                    {mode === "create" 
                      ? (parentAgentId 
                          ? "This agent will be added as a sub-agent to the parent agent"
                          : "Set up your agent's basic information")
                      : agent.description
                    }
                  </p>
                </div>
              </div>
              {mode === "edit" && (
                <div className="flex gap-2">
                  <Button
                    onClick={handleTest}
                    variant="outline"
                    className="gap-2"
                  >
                    <Play className="w-4 h-4" />
                    Test Agent
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={!isFormValid || isSaving}
                    className="gap-2"
                  >
                    <Save className="w-4 h-4" />
                    Save Changes
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Agent Details Form */}
        <Card className="border-border">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl font-gc-med text-foreground">Agent Details</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Configure your agent's basic information and settings
            </p>
          </CardHeader>
          <CardContent className="p-8 pt-0">
            <AgentBasicInfo
              agent={agent}
              setAgent={setAgent}
              applications={applications}
              isLoading={isLoading}
              onApplicationCreated={handleApplicationCreated}
            />
            
            {/* Footer with Create Button (only for create mode) */}
            {mode === "create" && (
              <div className="flex justify-end pt-6 mt-6">
                <Button
                  onClick={handleSave}
                  disabled={!isFormValid || isSaving}
                  className="gap-2"
                >
                  <Save className="w-4 h-4" />
                  {isSaving ? "Creating..." : "Create Agent"}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AgentBuilder; 